import { ReactElement } from 'react';
import { List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, styled } from '@mui/material';
import { ProductType, Product, OrderContext, Plan } from '@localstack/types';
import { formatBytes, formatMonetaryAmount, getProductEstimations, getProductVolume } from '@localstack/services';
import { ExternalLink } from '@localstack/constants';

import { PlanProductIcon } from '../../../display/PlanProductIcon';

export interface ProductsListProps {
  plan: Plan;
  products: Product[];
  context: OrderContext;
  isAdminMode: boolean;
}

const CustomAnchor = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const getSeatsLimitText = (plan: Plan, isAdminMode: boolean): JSX.Element | undefined => {
  if (!plan.plan_seat_limit) return;
  const hideContactLink = isAdminMode || !plan.updatable;

  return (
    <span>
      Up to {plan.plan_seat_limit} seats
      {!hideContactLink && (
        <>
          {' '}
          – Need more? <CustomAnchor href={ExternalLink.CONTACT_TEAM}>Contact our team</CustomAnchor>
        </>
      )}
    </span>
  );
};

export const ProductsList = ({ plan, products, context, isAdminMode }: ProductsListProps): ReactElement => {
  return (
    <List dense disablePadding>
      {products?.map((product) => (
        <ListItem key={product.id}>
          <ListItemAvatar>
            <PlanProductIcon productType={product.type} />
          </ListItemAvatar>
          <ListItemText
            primary={product.name}
            secondary={
              <>
                {product.type === ProductType.SEATS && getSeatsLimitText(plan, isAdminMode)}
                {product.type === ProductType.CI_USAGE &&
                  `${getProductVolume(product, context) ?? '∞'} CI Credits / month`}
                {product.type === ProductType.POD_USAGE && `${formatBytes(getProductVolume(product, context)) ?? '∞'}`}
                {product.type === ProductType.COMPUTE_USAGE &&
                  `${getProductVolume(product, context) ?? '∞'} Credits / month`}
              </>
            }
          />
          <ListItemSecondaryAction>
            <ListItemText primary={formatMonetaryAmount(getProductEstimations(product, context), plan.currency)} />
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );
};
